"use client";

import type React from "react";

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { useMediaQuery } from "react-responsive";
import { ChevronDown, Clock } from "lucide-react";
import { Icon } from "@iconify-icon/react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import SidebarLogo from "@/layouts/DashboardLayout/components/SidebarLogo";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import AppLogo from "@/components/Logo.tsx";
import { isAlwaysCollapsedPage, useSidebarLinks } from "@/hooks/use-sidebar-links.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { SidebarUserDropdown } from "@/layouts/DashboardLayout/components/SidebarUserDropdown.tsx";
import { WorkspaceSwitcher } from "@/features/workspaces/components/widget/workspace-switcher";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { cn } from "@/lib/utils.ts";
import { CoachMarker } from "@/components/CoachMarker.tsx";
import { useCoachMarkers } from "@/hooks/useCoachMarkers.ts";

// Update the animation constants for smoother transitions
const ANIMATION_DURATION = 0.2;
const ANIMATION_EASE = [0.25, 0.1, 0.25, 1.0]; // improved cubic bezier for smoother motion

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { linkGroups, isLinkActive } = useSidebarLinks();
  const { user } = useUserStore();
  const { state, setOpen } = useSidebar();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isMouseOver = useRef(false);
  const navigate = useNavigate();
  const isCollapsedPage = isAlwaysCollapsedPage(location.pathname);
  const { isMarkerOpen } = useCoachMarkers();
  const isCoachMarkOpen = isMarkerOpen("sidebar-navigation");

  // Track open state for each link with children
  const [openLinks, setOpenLinks] = useState<Record<string, boolean>>({});

  const isLargeScreen = useMediaQuery({ minWidth: 1700 });

  useEffect(() => {
    // Initialize links with children based on active state
    const initialOpenState: Record<string, boolean> = {};

    linkGroups.forEach((group) => {
      group.links.forEach((link) => {
        if (link.children?.length) {
          // Auto-expand if the link itself is active or any child is active
          initialOpenState[`${group.id}-${link.id}`] =
            isLinkActive(link.pattern) ||
            link.children.some((child) => isLinkActive(child.pattern));
        }
      });
    });

    setOpenLinks(initialOpenState);
  }, [linkGroups, location.pathname]);

  useEffect(() => {
    if ((isLargeScreen && !isCollapsedPage) || isMouseOver.current) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isCollapsedPage, isLargeScreen, isMobile]);

  // Only use mouse hover logic on smaller screens
  const handleMouseEnter = () => {
    if (isLargeScreen && !isCollapsedPage) return;

    isMouseOver.current = true;
    setTimeout(() => {
      if (state === "collapsed" && isMouseOver.current) {
        setOpen(true);
      }
    }, 300);
  };

  const handleMouseLeave = () => {
    if (isLargeScreen && !isCollapsedPage) return;

    isMouseOver.current = false;
    setTimeout(() => {
      if (state === "expanded" || isMobile) {
        setOpen(false);
      }
    }, 300);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setOpen(false);
    } else {
      // Only auto-expand on navigation for non-large screens
      setOpen(true);
    }
    // On large screens, don't change the sidebar state on navigation
  };

  // Improved renderGroupLabel function with better transitions
  const renderGroupLabel = (group: (typeof linkGroups)[0]) => {
    return (
      <SidebarGroupLabel
        className={`text-muted-foreground/70 flex h-5 w-full items-center ps-0! font-medium text-nowrap ${
          state === "collapsed" ? "justify-center" : "ps-2"
        } ${isMobile && "justify-start! gap-1"}`}
      >
        <motion.div
          layout
          className="absolute left-0 flex h-5 items-center"
          transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
        >
          {/* Use AnimatePresence to handle the transition between states */}
          <AnimatePresence mode="wait">
            {state === "collapsed" && !isMobile ? (
              <motion.div
                key="collapsed"
                animate={{ opacity: 1, width: 24 }}
                className="flex items-center justify-center"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: ANIMATION_DURATION * 0.75, ease: ANIMATION_EASE }}
              >
                <div className="bg-muted-foreground/20 absolute top-1/2 h-[1px] w-full -translate-y-[0.5px]" />
              </motion.div>
            ) : (
              <motion.div
                key="expanded"
                animate={{ opacity: 1, width: "auto" }}
                className="flex items-center gap-2 truncate"
                exit={{ opacity: 0, width: 0 }}
                initial={{ opacity: 0, width: 0 }}
                transition={{ duration: ANIMATION_DURATION * 0.75, ease: ANIMATION_EASE }}
              >
                <span className="block text-[11px] whitespace-nowrap">{group.label}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </SidebarGroupLabel>
    );
  };

  // Update the renderCollapsibleLink function to match the design
  const renderCollapsibleLink = (group: (typeof linkGroups)[0], link: (typeof group.links)[0]) => {
    const linkKey = `${group.id}-${link.id}`;
    const isOpen = openLinks[linkKey] || false;
    const active = isLinkActive(link.pattern);

    // Spring animation configuration for container
    const containerSpring = {
      type: "spring",
      stiffness: 200,
      damping: 35,
      mass: 1.2,
    };

    if (link.coachMark?.shouldShow) {
      return (
        <CoachMarker
          className="w-full"
          description={link.coachMark?.description || ""}
          id={link.coachMark?.id || ""}
          placement={link.coachMark?.placement || "right"}
          title={link.coachMark?.title || ""}
        >
          <Collapsible
            className="group/collapsible w-full"
            open={isOpen}
            onOpenChange={(open) => setOpenLinks((prev) => ({ ...prev, [linkKey]: open }))}
          >
            <CollapsibleTrigger asChild disabled={link.disabled}>
              <div className="relative h-9 w-full">
                <motion.div
                  layout
                  className="absolute inset-0"
                  transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                >
                  <SidebarMenuButton
                    className={cn(
                      "data-[state=active]:bg-foreground/20! data-[state=active]:text-accent-foreground! absolute inset-0 flex h-9 cursor-pointer items-center justify-between rounded-md ring-0! transition-all",
                      "hover:bg-primary/35",
                      state === "collapsed" ? "justify-center" : "px-2",
                      active
                        ? "bg-foreground/20! text-accent-foreground!"
                        : "text-muted-foreground",
                      isMobile && "justify-start!",
                    )}
                    disabled={link.disabled}
                    isActive={active}
                  >
                    <motion.div
                      layout
                      className="absolute left-0 flex items-center"
                      transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                    >
                      <div className="relative flex h-5 w-5 items-center justify-center">
                        <motion.div
                          layout
                          className={cn(
                            "flex items-center justify-center",
                            active ? "text-accent-foreground!" : "text-muted-foreground",
                          )}
                          transition={{ duration: ANIMATION_DURATION * 0.75, ease: ANIMATION_EASE }}
                        >
                          {link.icon}
                        </motion.div>
                      </div>

                      <motion.div
                        layout
                        animate={{ width: state === "expanded" || isMobile ? "auto" : 0 }}
                        className="overflow-hidden"
                        initial={false}
                        transition={{ duration: ANIMATION_DURATION * 0.75, ease: ANIMATION_EASE }}
                      >
                        <span className="ml-2 block text-[12px] font-medium whitespace-nowrap">
                          {link.title}
                        </span>
                      </motion.div>
                    </motion.div>

                    {/* Lock icon for disabled items */}
                    {link.disabled && (state === "expanded" || isMobile) && (
                      <motion.span
                        animate={{ opacity: 1 }}
                        className={cn(
                          "absolute -right-0.5 flex h-4 w-4 items-center justify-center",
                          isMobile && "ms-auto mr-8",
                        )}
                        initial={{ opacity: 0 }}
                        layout={"position"}
                        transition={{
                          duration: ANIMATION_DURATION,
                          ease: ANIMATION_EASE,
                          delay: 0.1,
                        }}
                      >
                        <Icon
                          className="text-muted-foreground"
                          height="0.75rem"
                          icon="lucide:lock"
                          width="0.75rem"
                        />
                      </motion.span>
                    )}

                    {/* Chevron indicator for expandable items */}
                    {link.children &&
                      link.children?.length > 0 &&
                      (state === "expanded" || isMobile) && (
                        <motion.span
                          animate={{ opacity: 1 }}
                          className={cn(
                            "absolute right-4 flex h-4 w-4 items-center justify-center",
                            isMobile && "ms-auto",
                          )}
                          initial={{ opacity: 0 }}
                          layout={"position"}
                          transition={{
                            duration: ANIMATION_DURATION,
                            ease: ANIMATION_EASE,
                            delay: 0.1,
                          }}
                        >
                          <motion.div
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </motion.div>
                        </motion.span>
                      )}
                  </SidebarMenuButton>
                </motion.div>
              </div>
            </CollapsibleTrigger>

            {/* Children content - always render when open, regardless of sidebar state */}
            {link.children && link.children?.length > 0 && (
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key={`${linkKey}-children`}
                    animate={{
                      height: "auto",
                      opacity: 1,
                      transition: {
                        height: containerSpring,
                        opacity: { duration: 0.2 },
                      },
                    }}
                    className="overflow-hidden"
                    exit={{
                      height: 0,
                      opacity: 0,
                      transition: {
                        height: { duration: 0.2, ease: [0.33, 1, 0.68, 1] },
                        opacity: { duration: 0.1 },
                      },
                    }}
                    initial={{ height: 0, opacity: 0 }}
                  >
                    <div className="relative">
                      {/* Vertical connecting line - animate its height based on sidebar state */}
                      <AnimatePresence>
                        {(state === "expanded" || isMobile) && (
                          <motion.div
                            key="vertical-line"
                            animate={{
                              height: "100%",
                              opacity: 1,
                            }}
                            className="bg-muted-foreground/20 absolute top-0 bottom-0 left-[12px] w-[1px]"
                            initial={{
                              height: "0%",
                              opacity: 0,
                            }}
                            transition={{
                              duration: 0.4,
                              ease: [0.4, 0.0, 0.2, 1],
                              delay: 0.1,
                            }}
                          />
                        )}
                      </AnimatePresence>

                      <motion.div
                        animate={{
                          paddingLeft: state === "expanded" || isMobile ? 24 : 0,
                        }}
                        className="flex flex-col"
                        transition={{
                          duration: 0.4,
                          ease: [0.4, 0.0, 0.2, 1],
                        }}
                      >
                        <AnimatePresence mode="popLayout">
                          {link.children.map((child, childIndex) => {
                            const childActive = isLinkActive(child.pattern);

                            const childContent = (
                              <motion.div
                                key={child.id}
                                layout
                                animate={{
                                  opacity: 1,
                                  scale: 1,
                                  transition: {
                                    duration: 0.6,
                                    ease: [0.4, 0.0, 0.2, 1],
                                    delay: childIndex * 0.08,
                                  },
                                }}
                                className="relative h-9 w-full"
                                exit={{
                                  opacity: 0,
                                  scale: 0.95,
                                  transition: {
                                    duration: 0.2,
                                    ease: [0.4, 0.0, 0.2, 1],
                                    delay:
                                      (link.children ? link.children.length - childIndex - 1 : 1) *
                                      0.03,
                                  },
                                }}
                                initial={{
                                  opacity: 0,
                                  scale: 0.95,
                                }}
                                transition={{
                                  layout: {
                                    duration: 0.5,
                                    ease: [0.4, 0.0, 0.2, 1],
                                  },
                                }}
                              >
                                <motion.div
                                  className="absolute inset-0"
                                  layout={"position"}
                                  transition={{
                                    duration: 0.5,
                                    ease: [0.4, 0.0, 0.2, 1],
                                  }}
                                >
                                  <SidebarMenuButton
                                    className={cn(
                                      "data-[state=active]:bg-foreground/20! data-[state=active]:text-accent-foreground! absolute inset-0 flex h-9 cursor-pointer items-center rounded-md",
                                      "hover:bg-primary/35 transition-all duration-300",
                                      childActive
                                        ? "bg-foreground/20! text-accent-foreground!"
                                        : "text-muted-foreground",
                                      isMobile && "justify-start!",
                                    )}
                                    disabled={child.soon || child.disabled}
                                    isActive={childActive}
                                    style={{
                                      justifyContent:
                                        state === "collapsed" && !isMobile
                                          ? "center"
                                          : "flex-start",
                                      paddingLeft: state === "collapsed" && !isMobile ? 0 : 8,
                                      paddingRight: state === "collapsed" && !isMobile ? 0 : 8,
                                      transition: "all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)",
                                    }}
                                    onClick={() => handleNavigation(child.path)}
                                  >
                                    <motion.div
                                      className="absolute left-0 flex w-full items-start"
                                      layout={"position"}
                                      style={{
                                        flexDirection:
                                          state === "collapsed" && !isMobile ? "column" : "row",
                                        gap: state === "collapsed" && !isMobile ? 2 : 0,
                                        transition: "all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1) 0.2s", // Added 0.2s delay
                                      }}
                                    >
                                      {child.icon && (
                                        <motion.div
                                          layout
                                          className="relative flex h-5 w-5 items-center justify-center"
                                          transition={{
                                            duration: 0.2,
                                            ease: [0.4, 0.0, 0.2, 1],
                                          }}
                                        >
                                          <motion.div
                                            layout
                                            className={cn(
                                              "flex items-center justify-center",
                                              childActive
                                                ? "text-accent-foreground!"
                                                : "text-muted-foreground",
                                            )}
                                            transition={{
                                              duration: 0.2,
                                              ease: [0.4, 0.0, 0.2, 1],
                                            }}
                                          >
                                            {child.icon}
                                          </motion.div>
                                        </motion.div>
                                      )}
                                      {(state === "expanded" || isMobile) && (
                                        <motion.div
                                          key={`text-${state}-${child.id}`}
                                          animate={{
                                            width: "100%",
                                            opacity: 1,
                                          }}
                                          className="w-full overflow-hidden"
                                          exit={{
                                            opacity: 0,
                                          }}
                                          initial={{
                                            width: 0,
                                            opacity: 0,
                                          }}
                                          layout={"preserve-aspect"}
                                          transition={{
                                            duration: state === "collapsed" ? 0 : 0.2, // Much faster exit
                                            delay:
                                              state === "expanded" ? 0.1 + childIndex * 0.05 : 0, // No delay on exit
                                          }}
                                        >
                                          <span
                                            className={cn(
                                              "relative flex w-[94.4%] justify-between text-[12px] font-medium whitespace-nowrap",
                                              child.icon ? "ml-2" : "",
                                            )}
                                          >
                                            <span
                                              className={`${(child.disabled || child.soon) && "max-w-[90%]"} truncate`}
                                            >
                                              {child.title}
                                            </span>
                                            <div className="ml-2 flex items-center gap-1">
                                              {child.soon &&
                                                (!child.disabled ? (
                                                  <span className="text-primary flex items-center">
                                                    <Clock className="h-3 w-3" />
                                                  </span>
                                                ) : null)}
                                              {child.disabled && (
                                                <span className="text-muted-foreground flex items-center">
                                                  <Icon
                                                    height="0.75rem"
                                                    icon="lucide:lock"
                                                    width="0.75rem"
                                                  />
                                                </span>
                                              )}
                                            </div>
                                          </span>
                                        </motion.div>
                                      )}
                                    </motion.div>
                                  </SidebarMenuButton>
                                </motion.div>
                              </motion.div>
                            );

                            // Wrap child with CoachMarker if it has coachMark property and should be shown
                            if (child.coachMark && child.coachMark.shouldShow) {
                              return (
                                <CoachMarker
                                  key={`coach-marker-${child.id}`}
                                  className="w-full"
                                  description={child.coachMark.description}
                                  id={child.coachMark.id}
                                  placement={child.coachMark.placement || "right"}
                                  title={child.coachMark.title}
                                >
                                  {childContent}
                                </CoachMarker>
                              );
                            }

                            return childContent;
                          })}
                        </AnimatePresence>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </Collapsible>
        </CoachMarker>
      );
    }

    return (
      <Collapsible
        className="group/collapsible w-full"
        open={isOpen}
        onOpenChange={(open) => setOpenLinks((prev) => ({ ...prev, [linkKey]: open }))}
      >
        <CollapsibleTrigger asChild disabled={link.disabled}>
          <div className="relative h-9 w-full">
            <motion.div
              layout
              className="absolute inset-0"
              transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
            >
              <SidebarMenuButton
                className={cn(
                  "data-[state=active]:bg-foreground/20! data-[state=active]:text-accent-foreground! absolute inset-0 flex h-9 cursor-pointer items-center justify-between rounded-md ring-0! transition-all",
                  "hover:bg-primary/35",
                  state === "collapsed" ? "justify-center" : "px-2",
                  active ? "bg-foreground/20! text-accent-foreground!" : "text-muted-foreground",
                  isMobile && "justify-start!",
                )}
                disabled={link.disabled}
                isActive={active}
              >
                <motion.div
                  layout
                  className="absolute left-0 flex items-center"
                  transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                >
                  <div className="relative flex h-5 w-5 items-center justify-center">
                    <motion.div
                      layout
                      className={cn(
                        "flex items-center justify-center",
                        active ? "text-accent-foreground!" : "text-muted-foreground",
                      )}
                      transition={{ duration: ANIMATION_DURATION * 0.75, ease: ANIMATION_EASE }}
                    >
                      {link.icon}
                    </motion.div>
                  </div>

                  <motion.div
                    layout
                    animate={{ width: state === "expanded" || isMobile ? "auto" : 0 }}
                    className="overflow-hidden"
                    initial={false}
                    transition={{ duration: ANIMATION_DURATION * 0.75, ease: ANIMATION_EASE }}
                  >
                    <span className="ml-2 block text-[12px] font-medium whitespace-nowrap">
                      {link.title}
                    </span>
                  </motion.div>
                </motion.div>

                {/* Lock icon for disabled items */}
                {link.disabled && (state === "expanded" || isMobile) && (
                  <motion.span
                    animate={{ opacity: 1 }}
                    className={cn(
                      "absolute -right-0.5 flex h-4 w-4 items-center justify-center",
                      isMobile && "ms-auto mr-8",
                    )}
                    initial={{ opacity: 0 }}
                    layout={"position"}
                    transition={{
                      duration: ANIMATION_DURATION,
                      ease: ANIMATION_EASE,
                      delay: 0.1,
                    }}
                  >
                    <Icon
                      className="text-muted-foreground"
                      height="0.75rem"
                      icon="lucide:lock"
                      width="0.75rem"
                    />
                  </motion.span>
                )}

                {/* Chevron indicator for expandable items */}
                {link.children &&
                  link.children?.length > 0 &&
                  (state === "expanded" || isMobile) && (
                    <motion.span
                      animate={{ opacity: 1 }}
                      className={cn(
                        "absolute right-4 flex h-4 w-4 items-center justify-center",
                        isMobile && "ms-auto",
                      )}
                      initial={{ opacity: 0 }}
                      layout={"position"}
                      transition={{
                        duration: ANIMATION_DURATION,
                        ease: ANIMATION_EASE,
                        delay: 0.1,
                      }}
                    >
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </motion.div>
                    </motion.span>
                  )}
              </SidebarMenuButton>
            </motion.div>
          </div>
        </CollapsibleTrigger>

        {/* Children content - always render when open, regardless of sidebar state */}
        {link.children && link.children?.length > 0 && (
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                key={`${linkKey}-children`}
                animate={{
                  height: "auto",
                  opacity: 1,
                  transition: {
                    height: containerSpring,
                    opacity: { duration: 0.2 },
                  },
                }}
                className="overflow-hidden"
                exit={{
                  height: 0,
                  opacity: 0,
                  transition: {
                    height: { duration: 0.2, ease: [0.33, 1, 0.68, 1] },
                    opacity: { duration: 0.1 },
                  },
                }}
                initial={{ height: 0, opacity: 0 }}
              >
                <div className="relative">
                  {/* Vertical connecting line - animate its height based on sidebar state */}
                  <AnimatePresence>
                    {(state === "expanded" || isMobile) && (
                      <motion.div
                        key="vertical-line"
                        animate={{
                          height: "100%",
                          opacity: 1,
                        }}
                        className="bg-muted-foreground/20 absolute top-0 bottom-0 left-[12px] w-[1px]"
                        initial={{
                          height: "0%",
                          opacity: 0,
                        }}
                        transition={{
                          duration: 0.4,
                          ease: [0.4, 0.0, 0.2, 1],
                          delay: 0.1,
                        }}
                      />
                    )}
                  </AnimatePresence>

                  <motion.div
                    animate={{
                      paddingLeft: state === "expanded" || isMobile ? 24 : 0,
                    }}
                    className="flex flex-col"
                    transition={{
                      duration: 0.4,
                      ease: [0.4, 0.0, 0.2, 1],
                    }}
                  >
                    <AnimatePresence mode="popLayout">
                      {link.children.map((child, childIndex) => {
                        const childActive = isLinkActive(child.pattern);

                        const childContent = (
                          <motion.div
                            key={child.id}
                            layout
                            animate={{
                              opacity: 1,
                              scale: 1,
                              transition: {
                                duration: 0.6,
                                ease: [0.4, 0.0, 0.2, 1],
                                delay: childIndex * 0.08,
                              },
                            }}
                            className="relative h-9 w-full"
                            exit={{
                              opacity: 0,
                              scale: 0.95,
                              transition: {
                                duration: 0.2,
                                ease: [0.4, 0.0, 0.2, 1],
                                delay:
                                  (link.children ? link.children.length - childIndex - 1 : 1) *
                                  0.03,
                              },
                            }}
                            initial={{
                              opacity: 0,
                              scale: 0.95,
                            }}
                            transition={{
                              layout: {
                                duration: 0.5,
                                ease: [0.4, 0.0, 0.2, 1],
                              },
                            }}
                          >
                            <motion.div
                              className="absolute inset-0"
                              layout={"position"}
                              transition={{
                                duration: 0.5,
                                ease: [0.4, 0.0, 0.2, 1],
                              }}
                            >
                              <SidebarMenuButton
                                className={cn(
                                  "data-[state=active]:bg-foreground/20! data-[state=active]:text-accent-foreground! absolute inset-0 flex h-9 cursor-pointer items-center rounded-md",
                                  "hover:bg-primary/35 transition-all duration-300",
                                  childActive
                                    ? "bg-foreground/20! text-accent-foreground!"
                                    : "text-muted-foreground",
                                  isMobile && "justify-start!",
                                )}
                                disabled={child.soon || child.disabled}
                                isActive={childActive}
                                style={{
                                  justifyContent:
                                    state === "collapsed" && !isMobile ? "center" : "flex-start",
                                  paddingLeft: state === "collapsed" && !isMobile ? 0 : 8,
                                  paddingRight: state === "collapsed" && !isMobile ? 0 : 8,
                                  transition: "all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)",
                                }}
                                onClick={() => handleNavigation(child.path)}
                              >
                                <motion.div
                                  className="absolute left-0 flex w-full items-start"
                                  layout={"position"}
                                  style={{
                                    flexDirection:
                                      state === "collapsed" && !isMobile ? "column" : "row",
                                    gap: state === "collapsed" && !isMobile ? 2 : 0,
                                    transition: "all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1) 0.2s", // Added 0.2s delay
                                  }}
                                >
                                  {child.icon && (
                                    <motion.div
                                      layout
                                      className="relative flex h-5 w-5 items-center justify-center"
                                      transition={{
                                        duration: 0.2,
                                        ease: [0.4, 0.0, 0.2, 1],
                                      }}
                                    >
                                      <motion.div
                                        layout
                                        className={cn(
                                          "flex items-center justify-center",
                                          childActive
                                            ? "text-accent-foreground!"
                                            : "text-muted-foreground",
                                        )}
                                        transition={{
                                          duration: 0.2,
                                          ease: [0.4, 0.0, 0.2, 1],
                                        }}
                                      >
                                        {child.icon}
                                      </motion.div>
                                    </motion.div>
                                  )}
                                  {(state === "expanded" || isMobile) && (
                                    <motion.div
                                      key={`text-${state}-${child.id}`}
                                      animate={{
                                        width: "100%",
                                        opacity: 1,
                                      }}
                                      className="w-full overflow-hidden"
                                      exit={{
                                        opacity: 0,
                                      }}
                                      initial={{
                                        width: 0,
                                        opacity: 0,
                                      }}
                                      layout={"preserve-aspect"}
                                      transition={{
                                        duration: state === "collapsed" ? 0 : 0.2, // Much faster exit
                                        delay: state === "expanded" ? 0.1 + childIndex * 0.05 : 0, // No delay on exit
                                      }}
                                    >
                                      <span
                                        className={cn(
                                          "relative flex w-[94.4%] justify-between text-[12px] font-medium whitespace-nowrap",
                                          child.icon ? "ml-2" : "",
                                        )}
                                      >
                                        <span
                                          className={`${(child.disabled || child.soon) && "max-w-[90%]"} truncate`}
                                        >
                                          {child.title}
                                        </span>
                                        <div className="ml-2 flex items-center gap-1">
                                          {child.soon &&
                                            (!child.disabled ? (
                                              <span className="text-primary flex items-center">
                                                <Clock className="h-3 w-3" />
                                              </span>
                                            ) : null)}
                                          {child.disabled && (
                                            <span className="text-muted-foreground flex items-center">
                                              <Icon
                                                height="0.75rem"
                                                icon="lucide:lock"
                                                width="0.75rem"
                                              />
                                            </span>
                                          )}
                                        </div>
                                      </span>
                                    </motion.div>
                                  )}
                                </motion.div>
                              </SidebarMenuButton>
                            </motion.div>
                          </motion.div>
                        );

                        // Wrap child with CoachMarker if it has coachMark property and should be shown
                        if (child.coachMark && child.coachMark.shouldShow) {
                          return (
                            <CoachMarker
                              key={`coach-marker-${child.id}`}
                              className="w-full"
                              description={child.coachMark.description}
                              id={child.coachMark.id}
                              placement={child.coachMark.placement || "right"}
                              title={child.coachMark.title}
                            >
                              {childContent}
                            </CoachMarker>
                          );
                        }

                        return childContent;
                      })}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </Collapsible>
    );
  };

  // Update the renderRegularLink function to match the design
  const renderRegularLink = (group: (typeof linkGroups)[0], link: (typeof group.links)[0]) => {
    const active = isLinkActive(link.pattern);

    const linkContent = (
      <div className="relative h-9 w-full">
        <motion.div
          layout
          className="absolute inset-0 w-full"
          transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
        >
          <SidebarMenuButton
            className={cn(
              "data-[state=active]:bg-foreground/20! data-[state=active]:text-accent-foreground! absolute inset-0 flex h-9 cursor-pointer items-center rounded-md transition-all",
              "hover:bg-primary/35",
              state === "collapsed" ? "justify-center" : "px-2",
              active ? "bg-foreground/20! text-accent-foreground!" : "text-muted-foreground",
              isMobile && "justify-start!",
            )}
            disabled={link.disabled || link.soon}
            isActive={active}
            onClick={() => handleNavigation(link.path)}
          >
            <motion.div
              className="absolute left-0 flex w-full items-center"
              transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
            >
              <div className="flex h-5 w-5 items-center justify-center">
                <motion.div
                  layout
                  className={cn(
                    "flex items-center justify-center",
                    active ? "text-accent-foreground!" : "text-muted-foreground",
                  )}
                  transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                >
                  {link.icon}
                </motion.div>
              </div>

              <motion.div
                layout
                animate={{ width: state === "expanded" || isMobile ? "100%" : 0 }}
                className="overflow-hidden"
                initial={false}
                transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
              >
                <span className="ml-2 flex items-center justify-between text-[12px] font-medium whitespace-nowrap">
                  <span className={`${link.disabled && "max-w-[90%]"} truncate`}>{link.title}</span>
                  <span>
                    {link.disabled && (state === "expanded" || isMobile) && (
                      <Icon
                        className="text-muted-foreground ml-2"
                        height="0.75rem"
                        icon="lucide:lock"
                        width="0.75rem"
                      />
                    )}
                    {link.soon &&
                      (state === "expanded" || isMobile) &&
                      (!link.disabled ? <Clock className="h-3 w-3" /> : null)}
                  </span>
                </span>
              </motion.div>
            </motion.div>
          </SidebarMenuButton>
        </motion.div>
      </div>
    );

    // Wrap link with CoachMarker if it has coachMark property and should be shown
    if (link.coachMark && link.coachMark.shouldShow) {
      return (
        <CoachMarker
          className="w-full"
          description={link.coachMark.description}
          id={link.coachMark.id}
          placement={link.coachMark.placement || "right"}
          title={link.coachMark.title}
        >
          {linkContent}
        </CoachMarker>
      );
    }

    return linkContent;
  };

  // Update the Sidebar component to match the design
  return (
    <Sidebar
      collapsible="icon"
      {...props}
      className="bg-background/95 border-border/10 border-r shadow-sm"
      onMouseEnter={!isCoachMarkOpen ? handleMouseEnter : undefined}
      onMouseLeave={!isCoachMarkOpen ? handleMouseLeave : undefined}
    >
      {/* Fixed header height with animated logo */}
      <SidebarHeader className="flex h-fit items-center justify-between p-4 pb-0">
        <motion.div
          layout
          className="transition-opacity duration-200 ease-in-out"
          transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
        >
          {!isMobile ? <SidebarLogo state={state} /> : <AppLogo className={"h-8"} />}
        </motion.div>
      </SidebarHeader>

      {/* Consistent padding in content area */}
      <SidebarContent className="overflow-hidden px-2 py-2">
        <ScrollArea className={`h-full ${state === "expanded" && "pe-1"} p-0`}>
          <LayoutGroup id="sidebar-content">
            <div className="flex flex-col">
              {linkGroups
                .sort((a, b) => a.id - b.id)
                .map((group) => (
                  <motion.div
                    key={group.id}
                    layout
                    className="relative"
                    transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                  >
                    <motion.div
                      layout
                      className="relative"
                      transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                    >
                      <SidebarGroup className="py-0">
                        {/* Fixed height and consistent padding for group header */}
                        <motion.div
                          className="relative z-20 h-5"
                          layout={"size"}
                          transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                        >
                          <motion.div
                            layout
                            className={`flex h-5 w-full items-center justify-between rounded-md bg-transparent ${
                              state === "expanded" || isMobile ? "pe-2" : "px-2"
                            } transition-all`}
                            transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                          >
                            {renderGroupLabel(group)}
                          </motion.div>
                        </motion.div>

                        {/* Group content with links - reduce the top padding */}
                        <motion.div
                          layout
                          className="pt-0.5" /* Changed from pt-1 to pt-0.5 */
                          transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
                        >
                          <SidebarGroupContent>
                            <SidebarMenu className={"gap-0"}>
                              {group.links.map((link) => (
                                <motion.div
                                  key={link.id}
                                  layout
                                  className="my-0.5" /* Reduced from my-1 to my-0.5 if needed */
                                  transition={{
                                    duration: ANIMATION_DURATION,
                                    ease: ANIMATION_EASE,
                                  }}
                                >
                                  <SidebarMenuItem>
                                    {link.children?.length
                                      ? renderCollapsibleLink(group, link)
                                      : renderRegularLink(group, link)}
                                  </SidebarMenuItem>
                                </motion.div>
                              ))}
                            </SidebarMenu>
                          </SidebarGroupContent>
                        </motion.div>
                      </SidebarGroup>
                    </motion.div>
                  </motion.div>
                ))}
            </div>
          </LayoutGroup>
        </ScrollArea>
      </SidebarContent>

      {/* Fixed footer padding with animated user dropdown */}
      <SidebarFooter className="h-fit">
        {isMobile && <WorkspaceSwitcher triggerClassName={"bg-transparent w-full! ps-0"} />}
        <motion.div
          layout
          className="h-10 transition-all"
          style={{ width: "100%", opacity: 1 }}
          transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
        >
          <SidebarUserDropdown isExpanded={state === "expanded" || isMobile} user={user} />
        </motion.div>
      </SidebarFooter>
    </Sidebar>
  );
}
